// src/components/agency/AgencyList.tsx
import React, { useState, useEffect } from 'react';
import {
  Search,
  Filter,
  Users,
  Calendar,
  AlertCircle,
  CheckCircle,
  ExternalLink,
  Trash2,
  BarChart3,
  MessageCircle,
  FileText,
  Key,
  Copy,
  Mail,
  Clock,
  Infinity,
  CreditCard,
  XCircle,
  AlertTriangle,
} from 'lucide-react';
import { FirebaseService } from '../../services/firebase';
import type { AgencyConfig } from '../../types/agency';

interface AgencyListProps {
  onAgencySelect: (agencyId: string) => void;
}

export const AgencyList: React.FC<AgencyListProps> = ({ onAgencySelect }) => {
  const [agencies, setAgencies] = useState<AgencyConfig[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterActive, setFilterActive] = useState<boolean | null>(true);
  const [filterSubscription, setFilterSubscription] = useState<string | null>(null);

  useEffect(() => {
    loadAgencies();
  }, [filterActive]);

  const loadAgencies = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await FirebaseService.listAgencies({
        limit: 50,
        filterActive: filterActive ?? undefined,
      });

      if (response.success) {
        setAgencies(response.agencies || []);
      } else {
        setError('代理店一覧の取得に失敗しました');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : '予期しないエラーが発生しました');
    } finally {
      setLoading(false);
    }
  };

  const handleDeactivateAgency = async (agencyId: string) => {
    if (!confirm('この代理店を無効化しますか？')) return;

    try {
      const response = await FirebaseService.deactivateAgency(agencyId, '管理者による無効化');
      if (response.success) {
        await loadAgencies(); // リスト更新
      } else {
        alert('代理店の無効化に失敗しました');
      }
    } catch (err) {
      alert(err instanceof Error ? err.message : '予期しないエラーが発生しました');
    }
  };

  const copyToClipboard = async (text: string, type: string) => {
    try {
      await navigator.clipboard.writeText(text);
      alert(`${type}をクリップボードにコピーしました`);
    } catch (err) {
      console.error('Failed to copy to clipboard:', err);
      alert('クリップボードへのコピーに失敗しました');
    }
  };

  const sendByEmail = (accessUrl: string, agencyName: string) => {
    const subject = encodeURIComponent(`【いきいきスマイル共済】アクセスURL - ${agencyName}`);
    const body = encodeURIComponent(
      `
${agencyName} 様

いつもお世話になっております。

いきいきスマイル共済シミュレーターのアクセスURLをお送りします。

【アクセスURL】
${accessUrl}

【有効期限】
無期限

このURLをクリックして、シミュレーターをご利用ください。
セキュリティのため、このURLは他の方との共有はお控えください。

ご不明な点がございましたら、お気軽にお問い合わせください。

よろしくお願いします。
    `.trim()
    );

    window.open(`mailto:?subject=${subject}&body=${body}`);
  };

  const generateAccessUrl = (shortTokenId: string) => {
    // シミュレーターへのアクセスURL
    return `https://simulator.ikiiki-smile-agency.jp/access?id=${shortTokenId}`;
  };

  const filteredAgencies = agencies.filter((agency) => {
    const matchesSearch =
      agency.agencyName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      agency.agencyId.toLowerCase().includes(searchTerm.toLowerCase());

    // 課金ステータスフィルター
    let matchesSubscription = true;
    if (filterSubscription !== null) {
      if (filterSubscription === 'none') {
        matchesSubscription = !agency.subscriptionStatus;
      } else {
        matchesSubscription = agency.subscriptionStatus === filterSubscription;
      }
    }

    return matchesSearch && matchesSubscription;
  });

  const formatDate = (timestamp: any) => {
    if (!timestamp) return '未設定';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('ja-JP', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatDateTime = (timestamp: any) => {
    if (!timestamp) return '未設定';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleString('ja-JP', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // 課金ステータスの表示用ヘルパー
  const getSubscriptionStatusBadge = (status?: string, cancelAtPeriodEnd?: boolean) => {
    if (!status) {
      return (
        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
          未登録
        </span>
      );
    }

    if (status === 'active' && cancelAtPeriodEnd) {
      return (
        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
          <AlertTriangle className="w-3 h-3 mr-1" />
          解約予定
        </span>
      );
    }

    switch (status) {
      case 'active':
        return (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
            <CreditCard className="w-3 h-3 mr-1" />
            有効
          </span>
        );
      case 'canceling':
        return (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
            <AlertTriangle className="w-3 h-3 mr-1" />
            解約予定
          </span>
        );
      case 'canceled':
        return (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
            <XCircle className="w-3 h-3 mr-1" />
            解約済み
          </span>
        );
      case 'past_due':
        return (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
            <AlertCircle className="w-3 h-3 mr-1" />
            支払い遅延
          </span>
        );
      case 'unpaid':
        return (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
            <AlertCircle className="w-3 h-3 mr-1" />
            未払い
          </span>
        );
      case 'trialing':
        return (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
            <Clock className="w-3 h-3 mr-1" />
            トライアル
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
            {status}
          </span>
        );
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <span className="ml-3 text-gray-600">代理店一覧を読み込んでいます...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* ヘッダー */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">代理店一覧</h2>
          <p className="text-sm text-gray-500 mt-1">登録されている代理店の管理と監視（短縮URL表示対応）</p>
        </div>
        <button
          onClick={loadAgencies}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          更新
        </button>
      </div>

      {/* 検索・フィルター */}
      <div className="mb-6 space-y-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
          <input
            type="text"
            placeholder="代理店名またはIDで検索..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>

        <div className="flex items-center space-x-4">
          <div className="flex items-center space-x-2">
            <Filter className="w-4 h-4 text-gray-500" />
            <span className="text-sm text-gray-700">状態:</span>
          </div>
          <div className="flex space-x-2">
            {[
              { value: null, label: '全て' },
              { value: true, label: 'アクティブ' },
              { value: false, label: '無効' },
            ].map((option) => (
              <button
                key={String(option.value)}
                onClick={() => setFilterActive(option.value)}
                className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                  filterActive === option.value
                    ? 'bg-blue-100 text-blue-800'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>

        {/* 課金ステータスフィルター */}
        <div className="flex items-center space-x-4">
          <div className="flex items-center space-x-2">
            <CreditCard className="w-4 h-4 text-gray-500" />
            <span className="text-sm text-gray-700">課金:</span>
          </div>
          <div className="flex flex-wrap gap-2">
            {[
              { value: null, label: '全て' },
              { value: 'active', label: '有効' },
              { value: 'canceling', label: '解約予定' },
              { value: 'canceled', label: '解約済み' },
              { value: 'none', label: '未登録' },
            ].map((option) => (
              <button
                key={String(option.value)}
                onClick={() => setFilterSubscription(option.value)}
                className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                  filterSubscription === option.value
                    ? 'bg-purple-100 text-purple-800'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* エラー表示 */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center space-x-2">
          <AlertCircle className="w-5 h-5 text-red-500" />
          <span className="text-red-700">{error}</span>
        </div>
      )}

      {/* 代理店一覧 */}
      {filteredAgencies.length === 0 ? (
        <div className="text-center py-12">
          <Users className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">代理店が見つかりません</h3>
          <p className="text-gray-500">
            {searchTerm ? '検索条件に一致する代理店がありません' : '登録されている代理店がありません'}
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredAgencies.map((agency) => {
            const accessUrl = agency.lastShortTokenId ? generateAccessUrl(agency.lastShortTokenId) : null;

            return (
              <div
                key={agency.agencyId}
                className="border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-4">
                      <h3 className="text-lg font-medium text-gray-900">{agency.agencyName}</h3>
                      <span className="text-sm text-gray-500">ID: {agency.agencyId}</span>
                      {agency.isActive ? (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          <CheckCircle className="w-3 h-3 mr-1" />
                          アクティブ
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                          <AlertCircle className="w-3 h-3 mr-1" />
                          無効
                        </span>
                      )}
                      {getSubscriptionStatusBadge(agency.subscriptionStatus, agency.cancelAtPeriodEnd)}
                    </div>

                    {/* 短縮URL情報 */}
                    <div className="mb-4">
                      {agency.lastShortTokenId && accessUrl ? (
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                          <div className="flex items-center space-x-2 mb-2">
                            <Key className="w-4 h-4 text-blue-600" />
                            <h4 className="text-sm font-semibold text-blue-900">アクセストークン情報</h4>
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                              <Infinity className="w-3 h-3 mr-1" />
                              無期限
                            </span>
                          </div>

                          <div className="space-y-2">
                            <div>
                              <div className="text-xs text-blue-700 mb-1">短縮ID:</div>
                              <div className="flex items-center space-x-2">
                                <code className="text-sm font-mono text-blue-800 bg-white px-2 py-1 rounded border">
                                  {agency.lastShortTokenId}
                                </code>
                                <button
                                  onClick={() => copyToClipboard(agency.lastShortTokenId!, '短縮ID')}
                                  className="p-1 text-blue-600 hover:text-blue-800 transition-colors"
                                  title="短縮IDをコピー"
                                >
                                  <Copy className="w-3 h-3" />
                                </button>
                              </div>
                            </div>

                            <div>
                              <div className="text-xs text-blue-700 mb-1">アクセスURL:</div>
                              <div className="flex items-center space-x-2">
                                <code className="flex-1 text-xs font-mono text-blue-800 bg-white px-2 py-1 rounded border break-all">
                                  {accessUrl}
                                </code>
                                <button
                                  onClick={() => copyToClipboard(accessUrl, 'アクセスURL')}
                                  className="p-1 text-blue-600 hover:text-blue-800 transition-colors"
                                  title="URLをコピー"
                                >
                                  <Copy className="w-3 h-3" />
                                </button>
                                <button
                                  onClick={() => sendByEmail(accessUrl, agency.agencyName)}
                                  className="p-1 text-green-600 hover:text-green-800 transition-colors"
                                  title="メールで送信"
                                >
                                  <Mail className="w-3 h-3" />
                                </button>
                                <button
                                  onClick={() => window.open(accessUrl, '_blank')}
                                  className="p-1 text-purple-600 hover:text-purple-800 transition-colors"
                                  title="テストアクセス"
                                >
                                  <ExternalLink className="w-3 h-3" />
                                </button>
                              </div>
                            </div>

                            {agency.lastTokenGenerated && (
                              <div className="text-xs text-blue-600">
                                <Clock className="w-3 h-3 inline mr-1" />
                                生成日時: {formatDateTime(agency.lastTokenGenerated)}
                              </div>
                            )}
                          </div>
                        </div>
                      ) : (
                        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                          <div className="flex items-center space-x-2 mb-1">
                            <AlertCircle className="w-4 h-4 text-yellow-600" />
                            <h4 className="text-sm font-semibold text-yellow-800">アクセストークン未生成</h4>
                          </div>
                          <p className="text-xs text-yellow-700">
                            この代理店にはまだアクセストークンが発行されていません。
                          </p>
                        </div>
                      )}
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-gray-600 mb-3">
                      <div className="flex items-center space-x-1">
                        <Calendar className="w-4 h-4" />
                        <span>作成日: {formatDate(agency.createdAt)}</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <Calendar className="w-4 h-4" />
                        <span>更新日: {formatDate(agency.updatedAt)}</span>
                      </div>
                    </div>

                    {/* ブランディング設定の表示 */}
                    {(agency.branding?.lineUrl || agency.branding?.applicationUrl) && (
                      <div className="flex items-center space-x-4 text-sm">
                        {agency.branding.lineUrl && (
                          <a
                            href={agency.branding.lineUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center space-x-1 text-green-600 hover:text-green-700"
                          >
                            <MessageCircle className="w-4 h-4" />
                            <span>LINE</span>
                            <ExternalLink className="w-3 h-3" />
                          </a>
                        )}
                        {agency.branding.applicationUrl && (
                          <a
                            href={agency.branding.applicationUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center space-x-1 text-blue-600 hover:text-blue-700"
                          >
                            <FileText className="w-4 h-4" />
                            <span>マインクラブ</span>
                            <ExternalLink className="w-3 h-3" />
                          </a>
                        )}
                      </div>
                    )}
                  </div>

                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => onAgencySelect(agency.agencyId)}
                      className="px-3 py-1 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition-colors flex items-center space-x-1"
                      title="詳細を表示"
                    >
                      <BarChart3 className="w-4 h-4" />
                      <span className="hidden sm:inline">詳細</span>
                    </button>

                    {agency.isActive && (
                      <button
                        onClick={() => handleDeactivateAgency(agency.agencyId)}
                        className="px-3 py-1 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition-colors flex items-center space-x-1"
                        title="代理店を無効化"
                      >
                        <Trash2 className="w-4 h-4" />
                        <span className="hidden sm:inline">無効化</span>
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* 統計サマリー */}
      <div className="mt-8 grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-blue-50 rounded-lg p-4">
          <div className="text-2xl font-bold text-blue-600">{filteredAgencies.length}</div>
          <div className="text-sm text-blue-800">表示中の代理店</div>
        </div>
        <div className="bg-green-50 rounded-lg p-4">
          <div className="text-2xl font-bold text-green-600">{filteredAgencies.filter((a) => a.isActive).length}</div>
          <div className="text-sm text-green-800">アクティブ代理店</div>
        </div>
        <div className="bg-yellow-50 rounded-lg p-4">
          <div className="text-2xl font-bold text-yellow-600">
            {filteredAgencies.filter((a) => a.lastShortTokenId).length}
          </div>
          <div className="text-sm text-yellow-800">トークン発行済み</div>
        </div>
        <div className="bg-purple-50 rounded-lg p-4">
          <div className="text-2xl font-bold text-purple-600">
            {filteredAgencies.filter((a) => a.subscriptionStatus === 'active').length}
          </div>
          <div className="text-sm text-purple-800">課金中</div>
        </div>
      </div>

      {/* 課金統計サマリー */}
      <div className="mt-4 grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-orange-50 rounded-lg p-4">
          <div className="text-2xl font-bold text-orange-600">
            {filteredAgencies.filter((a) => a.subscriptionStatus === 'canceling' || a.cancelAtPeriodEnd).length}
          </div>
          <div className="text-sm text-orange-800">解約予定</div>
        </div>
        <div className="bg-red-50 rounded-lg p-4">
          <div className="text-2xl font-bold text-red-600">
            {filteredAgencies.filter((a) => a.subscriptionStatus === 'canceled').length}
          </div>
          <div className="text-sm text-red-800">解約済み</div>
        </div>
        <div className="bg-gray-50 rounded-lg p-4">
          <div className="text-2xl font-bold text-gray-600">
            {filteredAgencies.filter((a) => !a.subscriptionStatus).length}
          </div>
          <div className="text-sm text-gray-800">課金未登録</div>
        </div>
        <div className="bg-indigo-50 rounded-lg p-4">
          <div className="text-2xl font-bold text-indigo-600">
            {
              filteredAgencies.filter((a) => a.subscriptionStatus === 'past_due' || a.subscriptionStatus === 'unpaid')
                .length
            }
          </div>
          <div className="text-sm text-indigo-800">支払い問題</div>
        </div>
      </div>
    </div>
  );
};
